import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

import {
  UseCasePreviewResponse,
  UseCaseProvisionFailure,
  UseCaseQuota,
  UseCaseValidationError,
} from 'src/app/core/models/use-case.models';

/**
 * Presentational rendering of a §9.2 preview plan: objects-to-create grouped by
 * section, quota rows, plan/input errors, and warnings. Shared by the Create
 * Use Case wizard's preview step (which also feeds it a §9.4 provision failure)
 * and the Use Case Template editor's Object-preview tab (proposal §8/§11). It
 * owns no fetching or state - the caller passes a resolved preview envelope.
 */
@Component({
  selector: 'app-use-case-plan-summary',
  templateUrl: './use-case-plan-summary.component.html',
  styleUrls: ['./use-case-plan-summary.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UseCasePlanSummaryComponent {
  @Input({ required: true }) preview!: UseCasePreviewResponse;
  // Wizard-only: a §9.4 provision failure surfaced above the plan. The editor
  // preview is write-free and never sets it.
  @Input() provisionFailure: UseCaseProvisionFailure | null = null;
  // The wizard maps input-layer errors back onto their step/field, so it shows
  // only plan-layer errors here; the editor has no steps and shows both.
  @Input() showInputErrors = false;

  /** Quota rows for the template. */
  quotaEntries(quota: UseCaseQuota): Array<{
    type: string;
    used?: number;
    available?: number;
    requested: number;
  }> {
    return Object.entries(quota).map(([type, entry]) => ({ type, ...entry }));
  }

  /**
   * The errors to display: plan-layer only for the wizard, both layers for the
   * editor (showInputErrors).
   *
   * @param errors the §9.3 error list from the preview envelope
   * @returns the errors to render
   */
  displayErrors(errors: UseCaseValidationError[]): UseCaseValidationError[] {
    return this.showInputErrors
      ? errors
      : errors.filter((error) => error.layer === 'plan');
  }
}
